import React from 'react';
import { Box, Chip, Paper, SvgIcon, Tooltip, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { IStoreState } from '@store/index';
import { Avatar } from '@components/common/Avatar';
import Utils from '@utils/index';
import styled from 'styled-components';
import EventTagLabel from './EventTagLabel';
import { DirectionsWalk, StorageOutlined, VideocamOutlined } from '@mui/icons-material';
import { isArray, isEmpty } from 'lodash';
import MotionIcon from '@components/common/Icon/MotionIcon';
import SafetyInfractionIcon from '@components/common/Icon/SafetyInfractionIcon';
import ZonesIcon from '@components/common/Icon/ZonesIcon';
import VehicleIcon from '@components/common/Icon/VehicleIcon';
import StreamEvents from '@classes/StreamEvents/StreamEvents';
import * as moment from 'moment';

const Card = styled(Paper) <{ selected: boolean; escalated: boolean }>`
  box-shadow: 0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%);
  background-color: ${props => props.selected ? 'rgba(0, 72, 227, 0.8)' : props.escalated ? 'rgba(255, 34, 0, 0.2)' : undefined};

  :hover {
    cursor: pointer;
    box-shadow: 0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 14%), 0px 1px 8px 0px rgb(0 0 0 / 12%);
  }

  user-select: none;
  -ms-user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;

  @keyframes color {
    0% {
      background-color: #fff;
    }
    50% {
      background-color: rgba(0, 72, 227, 0.08);
    }
    100% {
      background-color: #fff;
    }
  }

  @keyframes color_selected {
    0% {
      background-color: rgba(0, 72, 227, 0.4);
    }
    50% {
      background-color: rgba(0, 72, 227, 0.8);
    }
    100% {
      background-color: rgba(0, 72, 227, 0.4);
    }
  }
`;

const PulsingCircle = styled(Box) <{ width: string, height: string }>`
  content: '';
  width: ${props => props.width};
  height: ${props => props.height};
  outline: 2px solid rgba(0, 72, 227, 0.8);
  border-radius: 50%;
  position: absolute;
  animation: pulsate infinite 1s;

  @-webkit-keyframes pulsate {
    0% {
      -webkit-transform: scale(1, 1);
      opacity: 1;
    }
    100% {
      -webkit-transform: scale(10, 10);
      opacity: 0;
    }
  }
`

const CardText = styled(Typography) <{ selected: boolean }>`
  line-height: 1.59 !important;
  color: ${props => props.selected ? 'white' : undefined};
`;

const CardContent = styled(Box) <{ selected: boolean }>`
  color: ${props => props.selected ? 'white' : undefined};
`;

type EventCardProps = {
  event: VideoStreamEvent;
  selected: boolean;
  onEventClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, event: VideoStreamEvent) => void;
  zoneNames?: string[];
  usersSelectingEvent: Array<string>;
}

const EventCard: React.FC<EventCardProps> = (props) => {
  const { event, selected, onEventClick, zoneNames, usersSelectingEvent } = props;

  const sites = useSelector((store: IStoreState) => store.sites.sites);
  const streams = useSelector((store: IStoreState) => store.streams.streams);
  const users = useSelector((store: IStoreState) => store.users.users);
  const companies = useSelector((store: IStoreState) => store.companies.companies);
  const options = useSelector((store: IStoreState) => store.overviewView.overviewOptions);
  const rootFontSize = useSelector((store: IStoreState) => store.appView.rootFontSize);

  const [tooltipOpen, setTooltipOpen] = React.useState<boolean>(false);
  const [titleHovered, setTitleHovered] = React.useState<boolean>(false);
  const [descriptionHovered, setDescriptionHovered] = React.useState<boolean>(false);

  const eventRef = React.useRef(event);

  React.useEffect(() => { eventRef.current = event }, [event])
  React.useEffect(() => {
    setTooltipOpen(titleHovered || descriptionHovered);
  }, [titleHovered, descriptionHovered])

  const eventTitle = React.useMemo(() => {
    return StreamEvents.getTitle(event, zoneNames);
  }, [event, zoneNames])

  const eventIcon = React.useMemo(() => {
    const TYPE_TO_TITLE = new Map([
      ["Motion", <MotionIcon />],
      ["Safety infraction", <SafetyInfractionIcon />],
      ["Geofence triggered", <ZonesIcon />],
      ["Zone triggered", <ZonesIcon />],
      ["Person detected", <DirectionsWalk />],
      ["Vehicle detected", <VehicleIcon />]
    ]);

    return (
      TYPE_TO_TITLE.get(event.eventType)
    )
  }, [event.eventType])

  const siteName = React.useMemo(() => {
    const stream = streams.find(el => el.kvsName === event.sourceCamera);

    if (stream) {
      const site = sites.get(stream.companyId)?.find(el => el.siteId === stream.siteId);
      if (site) {
        return site.name
      }
    }

    return `Error finding the event site`;
  }, [streams, event.sourceCamera, sites]);

  const streamName = React.useMemo(() => {
    const stream = streams.find(el => el.kvsName === event.sourceCamera);
    const eventContainsChildren = isArray(event.children) && !isEmpty(event.children) && stream;

    if (stream) {
      if (eventContainsChildren) {
        const kvsNames = getKvsNames()
        const names: Set<string> = new Set();
        names.add(stream.defaultName || stream.kvsName)
        kvsNames.forEach((kvsName) => {
          const t = streams.find(el => el.kvsName === kvsName);
          if (t) names.add(t.defaultName || kvsName)
        })

        return Array.from(names).join(', ')
      }

      return stream.defaultName || stream.kvsName;
    }

    return `Error finding the event site`;
  }, [event.children, event.sourceCamera, streams])

  const relativeTime = React.useMemo(() => {
    return Utils.Date.getTimeAgo(Date.now() - (event.arrived ? event.arrived : Utils.Timestamp.edgeTimestampToMiliseconds(event.startDate)));
  }, [event.arrived, event.startDate])

  const absoluteTimeSiteTimezone = React.useMemo(() => {
    const now = moment.tz(Date.now(), StreamEvents.getCompanyAndSite(event, companies, sites).site?.timezone?.split(' ')[0] || "");
    const eDate = moment.tz(Utils.Timestamp.edgeTimestampToMiliseconds(event.endDate), StreamEvents.getCompanyAndSite(event, companies, sites).site?.timezone?.split(' ')[0] || "");

    const isSameDay = now.date() === eDate.date() && now.month() === eDate.month() && now.year() === eDate.year();
    const isSameYear = now.year() === eDate.year();
    return Utils.Date.format(new Date(Utils.Timestamp.edgeTimestampToMiliseconds(event.endDate)), isSameDay ? "h:mm:ss A" : isSameYear ? "MMM Do, h:mm:ss A" : "MMM Do, YYYY h:mm:ss A", StreamEvents.getCompanyAndSite(event, companies, sites).site?.timezone?.split(' ')[0]);
  }, [companies, event, sites])


  const absoluteUserTimezone = React.useMemo(() => {
    const now = moment.tz(Date.now(), moment.tz.guess());
    const eDate = moment.tz(Utils.Timestamp.edgeTimestampToMiliseconds(event.endDate), moment.tz.guess());

    const isSameDay = now.date() === eDate.date() && now.month() === eDate.month() && now.year() === eDate.year();
    const isSameYear = now.year() === eDate.year();
    return Utils.Date.format(new Date(Utils.Timestamp.edgeTimestampToMiliseconds(event.endDate)), isSameDay ? "h:mm:ss A" : isSameYear ? "MMM Do, h:mm:ss A" : "MMM Do, YYYY h:mm:ss A", moment.tz.guess());
  }, [event.endDate])

  function renderSelectingUsersAvatars(): JSX.Element[] {
    return getUsersSelecting().map((profile) => (
      <Tooltip key={profile.userId} title={Utils.User.getUserName(profile || {})}>
        <Box display="flex">
          <Avatar
            fontSize={rootFontSize * 0.5}
            lineHeight={1}
            disableGap
            user={profile}
            height={rootFontSize * 1}
            width={rootFontSize * 1}
          />
          <PulsingCircle
            height={`${rootFontSize * 1}px`}
            width={`${rootFontSize * 1}px`}
          />
        </Box>
      </Tooltip>
    ))
  }

  function getUsersSelecting(): User[] {
    let usersProfiles: User[] = [];
    usersSelectingEvent.forEach(id => {
      const target = users.find(el => el.userId === id);
      if (target) usersProfiles.push(target);
    })
    return usersProfiles;
  }

  function getTooltipTitle(type: "description" | "title"): JSX.Element[] | string {
    if (zoneNames && zoneNames.length > 1) {
      if (type === "description") return "";
      return zoneNames.map(el => <Typography key={el} variant="body2" >{el}</Typography>);
    }

    if (isArray(event.children) && !isEmpty(event.children)) {
      const kvsNames = getKvsNames()
      return kvsNames.map(kvsName => {
        const stream = streams.find(el => el.kvsName === kvsName)
        return <Typography key={kvsName} variant="body2" >{stream?.defaultName || stream?.kvsName || "Unknown"}</Typography>;
      })
    }

    return ""
  }

  function getKvsNames(): string[] {
    const kvsNamesSet: Set<string> = new Set<string>();
    kvsNamesSet.add(eventRef.current.sourceCamera);
    eventRef.current.children?.forEach((element) => {
      kvsNamesSet.add(element.sourceCamera);
    })
    return Array.from(kvsNamesSet);
  }

  const userData = users.find(el => el.userId === event.owner) as User;

  return (
    <Box
      paddingTop="0.5rem"
      paddingBottom="0.5rem"
      paddingLeft="0.3rem"
      paddingRight="1rem"
    >
      <Card
        selected={selected}
        escalated={Boolean(event.tags && event.tags[0] && event.tags[0].toString().includes("escalate"))}
        onClick={(e) => { onEventClick(e, event); }}
        style={{
          outline: getUsersSelecting().length > 0 ? '1px solid rgba(0, 72, 227, 0.8)' : undefined,
          animationName: event.ongoing ? selected ? "color_selected" : "color" : undefined,
          opacity: event.ongoing ? 0.5 : 1,
          animationIterationCount: event.ongoing ? "infinite" : undefined,
          animationDuration: '1s'
        }}
        data-cy={`event-card`}
      >
        <Box padding="0.5rem" display="grid" gridTemplateRows="1fr 0px">
          <Box display="grid" gridTemplateColumns="1fr 7fr" gap="0.5rem">
            <Box display="grid" gridTemplateRows="auto auto auto" height="100%" justifyContent="center" borderRight="1px solid rgba(0, 0, 0, 0.12)">
              <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" width="100%">
                <SvgIcon fontSize='small' style={{ color: selected ? 'white' : 'black' }}>
                  {eventIcon}
                </SvgIcon>
              </Box>
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%" height="100%">
                <Typography textAlign="center" color={selected ? 'white' : 'black'} variant="subtitle2">{Utils.Timestamp.getDifferenceInSeconds(Utils.Timestamp.edgeTimestampToMiliseconds(event.startDate), event?.endDate || event?.currentEnd || Date.now(), true)}</Typography>
              </Box>
              {event.sourceType && event.sourceType !== "ubiquiti" &&
                <Tooltip title={event.sourceType === "edge" ? "Event generated by an edge device" : "Event generated without an edge device"}>
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    {event.sourceType === "edge" &&
                      <SvgIcon style={{ color: selected ? 'white' : 'black' }} fontSize='small' >
                        <StorageOutlined fontSize='small' />
                      </SvgIcon>
                    }
                    {event.sourceType === "camera" &&
                      <SvgIcon style={{ color: selected ? 'white' : 'black' }} fontSize='small' >
                        <VideocamOutlined fontSize='small' />
                      </SvgIcon>
                    }
                  </Box>
                </Tooltip>
              }
            </Box>
            <Box
              display="grid"
              gap="0.4rem"
              gridTemplateRows="1fr 1fr 1fr 0px"
            >
              <Box
                width="100%"
                height="100%"
                display="grid"
                gridTemplateColumns="auto max-content"
                gap="0.5rem"
                alignItems="center"
              >
                <Tooltip
                  open={tooltipOpen}
                  disableInteractive
                  title={getTooltipTitle("title")}
                >
                  <CardContent
                    onMouseEnter={() => setTitleHovered(true)}
                    onMouseLeave={() => setTitleHovered(false)}
                    selected={selected}
                    data-cy="event-card-title"
                  >
                    <Box width="100%" height="100%" display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                      <Typography
                        noWrap
                        maxWidth={(options.time === "absolute_site_timezone" && absoluteTimeSiteTimezone.length > 9) ? '7rem' :
                          options.time === "absolute_user_timezone" && absoluteUserTimezone.length > 9 ? '7rem' : '13rem'
                        }
                        variant="subtitle2"
                      >
                        {eventTitle}
                      </Typography>
                      {event?.sourceType === 'ubiquiti' &&
                        <Chip
                          label="UniFi Protect"
                          size="small"
                          sx={{
                            my: 0,
                            height: 'auto',
                            alignSelf: 'stretch',
                            color: 'primary',
                            backgroundColor: '#EAF3FE',
                          }}
                        />
                      }
                    </Box>
                  </CardContent>
                </Tooltip>
                <Typography
                  style={{ lineHeight: 0, color: selected ? 'white' : undefined }}
                  variant="overline"
                  data-cy="event-card-time"
                >
                  {options.time === "absolute_site_timezone" &&
                    absoluteTimeSiteTimezone
                  }
                  {options.time === "absolute_user_timezone" &&
                    absoluteUserTimezone
                  }
                  {options.time === "relative" &&
                    relativeTime
                  }
                </Typography>
              </Box>
              <Box
                width="100%"
                height="100%"
                display="grid"
                gridTemplateColumns="auto max-content"
                gap="0.5rem"
                alignItems="center"
              >
                <CardText
                  onMouseEnter={() => setDescriptionHovered(true)}
                  onMouseLeave={() => setDescriptionHovered(false)}
                  selected={selected}
                  noWrap={true}
                  variant="body2"
                  data-cy="event-card-desc"
                >
                  <Box display="flex" flexDirection="row" alignItems="center">
                    {siteName}
                  </Box>
                </CardText>
                {Boolean(event.tags) && !isEmpty(event.tags) && <EventTagLabel event={event} selected={selected} />}
              </Box>
              <Box
                width="100%"
                height="100%"
                display="grid"
                gridTemplateColumns="auto min-content"
                gap="0.5rem"
                alignItems="center"
              >
                <CardText
                  onMouseEnter={() => setDescriptionHovered(true)}
                  onMouseLeave={() => setDescriptionHovered(false)}
                  selected={selected}
                  noWrap={true}
                  variant="body2"
                  data-cy="event-card-desc"
                >
                  {streamName}
                </CardText>
                <Tooltip title={Utils.User.getUserName(userData || {})}>
                  <Box marginLeft="0.25rem" display="flex" alignItems="center" height="100%">
                    {event.owner &&
                      <Avatar
                        fontSize={rootFontSize * 0.8}
                        lineHeight={1}
                        disableGap
                        user={userData}
                        height={rootFontSize * 1.4}
                        width={rootFontSize * 1.4}
                      />
                    }
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Box>
          <Box
            width="100%"
            height="100%"
            display="grid"
            gridTemplateColumns="auto max-content max-content max-content"
            alignItems="center"
          >
            <Box position="relative" width="100%" display="flex" alignItems="center" justifyContent="center">
              {renderSelectingUsersAvatars()}
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  )
}

export default EventCard;
